import React, { useEffect, useMemo, useState } from "react";
import Select, { components } from "react-select";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { sendMessage } from "../../../services/messageServices";
import { getAllUsers } from "../../../services/userServices";
import { toAbsoluteUserPhotoUrl } from "../../../services/userPhotoServices";

import { IoIosSend } from "react-icons/io";
import { FaSpinner, FaTimes } from "react-icons/fa";

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
  "clean",
];

const SUBJECT_OPTIONS = [
  "Adhésion",
  "Dossier d'inscription",
  "Règlement",
  "Informations cours",
  "Absence",
  "Inscription compétition",
  "Perte d'objet",
  "Autre (saisir)",
];

function normalize(value = "") {
  return String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function isEnabled(value) {
  return value === true || value === 1 || value === "1";
}

function roleMeta(roleId) {
  if (Number(roleId) === 1) return { label: "Administrateur", tone: "red" };
  if (Number(roleId) === 3) return { label: "Enseignant", tone: "green" };
  if (Number(roleId) === 2) return { label: "Utilisateur", tone: "slate" };
  return { label: "", tone: "slate" };
}

function roleBadgeClass(tone) {
  switch (tone) {
    case "red":
      return "bg-red-600 text-white";
    case "green":
      return "bg-emerald-600 text-white";
    default:
      return "bg-slate-600 text-white";
  }
}

const CustomOption = (props) => {
  const { data } = props;

  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        <img
          src={data.photo || "/img/user_avatar.jpg"}
          alt={data.label}
          className="h-8 w-8 rounded-full object-cover ring-1 ring-slate-300/60"
        />

        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold">{data.name}</span>

            {data.roleLabel && (
              <span
                className={`inline-flex shrink-0 items-center rounded-full px-2 py-[2px] text-[10px] font-bold ${roleBadgeClass(
                  data.roleTone
                )}`}
              >
                {data.roleLabel}
              </span>
            )}
          </div>

          {data.secondary && (
            <p className="truncate text-[11px] opacity-70">{data.secondary}</p>
          )}
        </div>
      </div>
    </components.Option>
  );
};

const CustomSingleValue = (props) => {
  const { data } = props;

  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-2">
        <img
          src={data.photo || "/img/user_avatar.jpg"}
          alt={data.label}
          className="h-6 w-6 rounded-full object-cover ring-1 ring-slate-300/60"
        />

        <span className="truncate text-sm font-semibold">{data.name}</span>
      </div>
    </components.SingleValue>
  );
};

const makeFilterOption = () => (option, rawInput) => {
  const input = normalize(rawInput);

  if (!input) return true;

  const data = option.data || {};
  const haystack = [
    data.label,
    data.name,
    data.secondary,
    data.roleLabel,
  ]
    .map(normalize)
    .join(" ");

  return haystack.includes(input);
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: 16,
    backgroundColor: "transparent",
    borderColor: state.isFocused ? "#7c3aed" : "rgba(148,163,184,.45)",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(124,58,237,.20)" : "none",
    minHeight: 48,
    ":hover": { borderColor: "#7c3aed" },
    color: "inherit",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 16,
    overflow: "hidden",
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? "rgba(124,58,237,.12)"
      : state.isSelected
      ? "rgba(124,58,237,.20)"
      : "transparent",
    color: "inherit",
    ":active": { backgroundColor: "rgba(124,58,237,.20)" },
  }),
  singleValue: (base) => ({ ...base, color: "inherit" }),
  placeholder: (base) => ({ ...base, color: "rgb(148 163 184)" }),
  input: (base) => ({ ...base, color: "inherit" }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

export default function CreateMessageForm({
  onSuccess,
  show,
  onHide,
  userId,
  userRole,
  initialRecipientId = "",
  initialSubject = "",
}) {
  const [messageData, setMessageData] = useState({
    recipient_id: initialRecipientId || "",
    subject: initialSubject || "",
    content: "",
  });

  const [recipients, setRecipients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const initialSubjectKnown = useMemo(
    () =>
      initialSubject &&
      SUBJECT_OPTIONS.some(
        (option) => option.toLowerCase() === initialSubject.toLowerCase()
      ),
    [initialSubject]
  );

  const [subjectChoice, setSubjectChoice] = useState(
    initialSubject
      ? initialSubjectKnown
        ? initialSubject
        : "Autre (saisir)"
      : ""
  );

  const [customSubject, setCustomSubject] = useState(
    initialSubject && !initialSubjectKnown ? initialSubject : ""
  );

  useEffect(() => {
    if (!show) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape" && !sending) {
        onHide?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onHide, sending, show]);

  useEffect(() => {
    if (!show) return;

    let mounted = true;

    async function fetchRecipients() {
      try {
        setIsLoading(true);

        const list = await getAllUsers();
        let users = Array.isArray(list) ? list : [];

        if (userRole === "user") {
          users = users.filter(
            (user) =>
              [1, 3].includes(Number(user.role_id)) &&
              isEnabled(user.receive_messages)
          );
        }

        if (mounted) {
          setRecipients(users);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des destinataires:", error);
        toast.error("Erreur lors du chargement des destinataires.");
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    fetchRecipients();

    return () => {
      mounted = false;
    };
  }, [show, userRole]);

  useEffect(() => {
    setMessageData({
      recipient_id: initialRecipientId || "",
      subject: initialSubject || "",
      content: "",
    });

    const known =
      initialSubject &&
      SUBJECT_OPTIONS.some(
        (option) => option.toLowerCase() === initialSubject.toLowerCase()
      );

    setSubjectChoice(
      initialSubject ? (known ? initialSubject : "Autre (saisir)") : ""
    );

    setCustomSubject(initialSubject && !known ? initialSubject : "");
  }, [initialRecipientId, initialSubject, show]);

  const options = useMemo(() => {
    return recipients.map((recipient) => {
      const photo = toAbsoluteUserPhotoUrl(
        recipient.photoURL || recipient.photo_url || "/img/user_avatar.jpg"
      );

      const fullName = `${recipient.nom || ""} ${recipient.prenom || ""}`.trim();

      const { label: roleLabel, tone: roleTone } = roleMeta(recipient.role_id);

      const secondary =
        userRole === "user"
          ? ""
          : recipient.adherents?.length
          ? `Adhérents : ${recipient.adherents
              .map((adherent) => `${adherent.prenom?.trim()} ${adherent.nom}`)
              .join(", ")}`
          : "";

      return {
        value: recipient.id,
        label: fullName,
        name: fullName,
        photo,
        roleLabel,
        roleTone,
        secondary,
      };
    });
  }, [recipients, userRole]);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === messageData.recipient_id) || null,
    [messageData.recipient_id, options]
  );

  const handleSubjectChoice = (event) => {
    const value = event.target.value;

    setSubjectChoice(value);

    if (value === "Autre (saisir)") {
      setMessageData((previous) => ({
        ...previous,
        subject: customSubject || "",
      }));
    } else {
      setMessageData((previous) => ({
        ...previous,
        subject: value,
      }));
    }
  };

  const handleCustomSubject = (event) => {
    const value = event.target.value;

    setCustomSubject(value);

    if (subjectChoice === "Autre (saisir)") {
      setMessageData((previous) => ({
        ...previous,
        subject: value,
      }));
    }
  };

  const finalSubject =
    subjectChoice === "Autre (saisir)"
      ? customSubject.trim()
      : String(messageData.subject || "").trim();

  const canSubmit =
    Boolean(messageData.recipient_id) &&
    Boolean(finalSubject) &&
    Boolean(messageData.content) &&
    messageData.content !== "<p><br></p>";

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) {
      toast.error("Veuillez remplir tous les champs.");
      return;
    }

    try {
      setSending(true);

      await sendMessage({
        ...messageData,
        sender_id: userId,
        subject: finalSubject,
      });

      toast.success("Message envoyé avec succès.");
      onSuccess?.();
      onHide?.();
    } catch (error) {
      console.error("Erreur lors de l'envoi du message:", error);
      toast.error("Erreur lors de l'envoi du message.");
    } finally {
      setSending(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-end justify-center sm:items-center sm:p-4">
      <div
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
        onClick={sending ? undefined : onHide}
      />

      <section className="relative z-[121] flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-[2rem] bg-white shadow-2xl dark:bg-slate-950 sm:max-w-3xl sm:rounded-[2rem]">
        <header className="relative overflow-hidden bg-slate-950 px-5 py-5 text-white sm:px-7">
          <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-sky-500/30 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-20 h-44 w-44 rounded-full bg-violet-500/30 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-200">
                Nouveau message
              </p>

              <h2 className="mt-2 text-xl font-bold sm:text-2xl">
                Composer un message
              </h2>

              <p className="mt-2 text-sm leading-6 text-slate-200">
                Sélectionne un destinataire, choisis un objet puis rédige ton
                message.
              </p>
            </div>

            <button
              type="button"
              onClick={onHide}
              disabled={sending}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
            <div>
              <label className="mb-1.5 block text-sm font-bold text-slate-900 dark:text-slate-100">
                Destinataire
              </label>

              {isLoading ? (
                <div className="h-12 w-full animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
              ) : (
                <Select
                  classNamePrefix="rs"
                  name="recipient_id"
                  options={options}
                  value={selectedOption}
                  onChange={(option) =>
                    setMessageData((previous) => ({
                      ...previous,
                      recipient_id: option?.value || "",
                    }))
                  }
                  placeholder="Rechercher un destinataire…"
                  isClearable
                  components={{
                    Option: CustomOption,
                    SingleValue: CustomSingleValue,
                  }}
                  filterOption={makeFilterOption()}
                  noOptionsMessage={() => "Aucun résultat"}
                  styles={selectStyles}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : undefined
                  }
                />
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[260px_minmax(0,1fr)]">
              <div>
                <label className="mb-1.5 block text-sm font-bold text-slate-900 dark:text-slate-100">
                  Objet
                </label>

                <select
                  value={subjectChoice}
                  onChange={handleSubjectChoice}
                  className="min-h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-900 outline-none transition focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  required
                >
                  <option value="">Sélectionnez une option</option>

                  {SUBJECT_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {subjectChoice === "Autre (saisir)" && (
                <div>
                  <label className="mb-1.5 block text-sm font-bold text-slate-900 dark:text-slate-100">
                    Objet personnalisé
                  </label>

                  <input
                    type="text"
                    value={customSubject}
                    onChange={handleCustomSubject}
                    placeholder="Ex. : Question sur la tenue, demande d’info…"
                    className="min-h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-900 outline-none transition focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-bold text-slate-900 dark:text-slate-100">
                Contenu
              </label>

              <div className="overflow-hidden rounded-2xl border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900 [&_.ql-editor]:min-h-[180px] [&_.ql-toolbar]:border-0 [&_.ql-container]:border-0">
                <ReactQuill
                  theme="snow"
                  value={messageData.content}
                  onChange={(content) =>
                    setMessageData((previous) => ({
                      ...previous,
                      content,
                    }))
                  }
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Rédigez votre message ici…"
                />
              </div>
            </div>
          </div>

          <footer className="sticky bottom-0 border-t border-slate-200 bg-white/95 p-3 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:p-4">
            <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
              <button
                type="button"
                onClick={onHide}
                disabled={sending}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={!canSubmit || sending || isLoading}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-violet-600 px-4 text-sm font-bold text-white transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? <FaSpinner className="animate-spin" /> : <IoIosSend />}
                {sending ? "Envoi…" : "Envoyer"}
              </button>
            </div>
          </footer>
        </form>
      </section>
    </div>
  );
}